import { ReactNode, PropsWithChildren } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';

import { GlobalFooter } from '@/patterns/global-footer/src/global-footer';

import { PricingPageFallback } from './pricing-page-fallback.tsx';

export interface PricingPageLayoutProps extends PropsWithChildren {
	nav?: ReactNode;
	banner?: ReactNode;
	hero?: ReactNode;
	bgColor?: string;
	hasGlobalFooter?: boolean;
	showFallback?: boolean;
	/**
	 * Opt-out of the default inline padding on the `<main>` element. Some
	 * calculator/full-bleed pages (e.g. the usage-based pricing calculator)
	 * need to reach the full width of the container without the shared
	 * 30px/60px inline padding this layout applies to standard pricing pages.
	 */
	noInlinePadding?: boolean;
}

export const PricingPageLayout = ({
	nav,
	banner,
	hero,
	bgColor,
	hasGlobalFooter = true,
	showFallback = false,
	noInlinePadding = false,
	children,
}: PricingPageLayoutProps) => {
	return (
		<>
			{nav && nav}
			{!showFallback && banner}
			{!showFallback && hero}
			<div style={{ background: bgColor }} css={pricingPageLayoutOuterCss}>
				{showFallback && <PricingPageFallback />}

				{!showFallback && children && (
					<main
						css={[pricingPageLayoutInnerCss, noInlinePadding && pricingPageLayoutInnerNoPaddingCss]}
						id="content"
					>
						{children}
					</main>
				)}
			</div>
			{hasGlobalFooter && (
				<div css={footerCss}>
					<GlobalFooter />
				</div>
			)}
		</>
	);
};

const footerCss = css({
	marginBlockStart: '64px',
	[media.below.sm]: {
		marginBlockStart: '48px',
	},
});

const pricingPageLayoutOuterCss = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	paddingBlockStart: '64px',
	paddingBlockEnd: '64px',
	[media.below.md]: {
		paddingBlockStart: '48px',
	},
	[media.below.sm]: {
		paddingBlockStart: '32px',
	},
});

const pricingPageLayoutInnerCss = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '64px',
	maxWidth: '1152px',
	width: '100% !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *:nth-of-type(2)': {
		marginBlockStart: '-16px',
	},
	[media.above.sm]: {
		paddingInlineStart: '30px',
		paddingInlineEnd: '30px',
	},
	[media.below.sm]: {
		paddingInlineStart: '60px',
		paddingInlineEnd: '60px',
		gap: '48px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > *:nth-of-type(2)': {
			marginBlockStart: '-12px',
		},
	},
	[media.below.xs]: {
		paddingInlineStart: '30px',
		paddingInlineEnd: '30px',
	},
});

// Opt-in override for full-bleed pricing pages (e.g. usage-based pricing
// calculator). Strips the inline padding at every breakpoint so the child
// content can span the full inner-max-width.
const pricingPageLayoutInnerNoPaddingCss = css({
	paddingInlineStart: 0,
	paddingInlineEnd: 0,
	[media.above.sm]: {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	[media.below.sm]: {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	[media.below.xs]: {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
});
